// auditService.js — Audit log service
import { supabase, isSupabaseConfigured } from '../supabaseClient';

/**
 * Log an audit event
 * @param {Object} params
 * @param {string} params.action — CREATE, UPDATE, DELETE, APPROVE, REJECT, LOGIN, LOGOUT, VIEW
 * @param {string} params.tableName
 * @param {string} params.recordId
 * @param {Object} params.oldValues
 * @param {Object} params.newValues
 * @param {Object} params.metadata — extra info like company_id, reason, ip
 */
export const logAuditEvent = async ({
  action,
  tableName,
  recordId,
  oldValues = null,
  newValues = null,
  metadata = {},
}) => {
  if (!isSupabaseConfigured()) {
    console.warn('[AuditService] Supabase not configured, skipping audit log');
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    // Use SECURITY DEFINER RPC to bypass RLS and ensure reliable logging
    const { data: auditId, error } = await supabase.rpc('log_audit_event', {
      p_action: action,
      p_table_name: tableName,
      p_record_id: String(recordId),
      p_old_values: oldValues,
      p_new_values: newValues,
      p_metadata: metadata,
    });

    if (!error) {
      return { success: true, error: null, auditId };
    }

    // Fallback to direct insert if RPC doesn't exist yet (migration path)
    if (error.message?.includes('function') || error.message?.includes('does not exist') || error.code === '42883') {
      console.warn('[AuditService] RPC not available, falling back to direct insert');
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      let userRole = null;
      let userEmail = user?.email || null;
      if (user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        userRole = profile?.role || null;
      }
      const auditRecord = {
        user_id: user?.id || null,
        user_email: userEmail,
        user_role: userRole,
        action,
        table_name: tableName,
        record_id: String(recordId),
        old_values: oldValues,
        new_values: newValues,
        metadata,
      };
      const { error: insertError } = await supabase.from('audit_logs').insert([auditRecord]);
      if (insertError) throw insertError;
      return { success: true, error: null };
    }

    throw error;
  } catch (err) {
    console.error('[AuditService] Failed to log audit event:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Fetch audit logs with filters
 */
export const getAuditLogs = async ({
  tableName,
  recordId,
  userId,
  action,
  startDate,
  endDate,
  companyId,
  excludeSuperAdmin = false,
  limit = 50,
  offset = 0,
}) => {
  if (!isSupabaseConfigured()) {
    return { success: false, data: [], count: 0, error: 'Supabase not configured' };
  }

  try {
    let query = supabase
      .from('audit_logs')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (tableName) query = query.eq('table_name', tableName);
    if (recordId) query = query.eq('record_id', recordId);
    if (userId) query = query.eq('user_id', userId);
    if (action) query = query.eq('action', action);
    if (startDate) query = query.gte('created_at', startDate);
    if (endDate) query = query.lte('created_at', endDate);
    if (companyId) {
      query = query.eq('metadata->>company_id', companyId);
    }
    if (excludeSuperAdmin) {
      query = query.not('user_role', 'eq', 'super_admin');
    }

    const { data, error, count } = await query;
    if (error) throw error;

    return { success: true, data: data || [], count: count || 0, error: null };
  } catch (err) {
    console.error('[AuditService] Error fetching audit logs:', err);
    return { success: false, data: [], count: 0, error: err.message };
  }
};

/**
 * Get audit logs for a specific employee (across all related tables)
 */
export const getAuditLogsForEmployee = async (employeeId, { limit = 50, offset = 0 } = {}) => {
  if (!isSupabaseConfigured()) {
    return { success: false, data: [], count: 0, error: 'Supabase not configured' };
  }

  try {
    // Fetch logs where record_id matches employee_id or metadata contains employee_id
    let query = supabase
      .from('audit_logs')
      .select('*', { count: 'exact' })
      .or(`record_id.eq.${employeeId},metadata->>employee_id.eq.${employeeId}`)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) throw error;

    return { success: true, data: data || [], count: count || 0, error: null };
  } catch (err) {
    console.error('[AuditService] Error fetching employee audit logs:', err);
    return { success: false, data: [], count: 0, error: err.message };
  }
};
